import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ref, onValue, set, onDisconnect } from 'firebase/database';
import { database } from '../config/firebase';
import { v4 as uuidv4 } from 'uuid';
import { BrainstormingBoard, subscribeToBoard, initBoardIfMissing, updateBoard } from '../utils/boardService';
import defaultProfile from '../assets/default-profile.png';
import { PlusIcon } from '@heroicons/react/24/solid';
import { PencilIcon } from '@heroicons/react/24/solid';
import { Stage, Layer, Line } from 'react-konva';
import { storage } from '../config/firebase';
import KonvaStickyNote from '../components/KonvaStickyNote';
import { updateStickyNotePosition } from '../utils/boardService';
import KonvaMindNode from '../components/KonvaMindNode';
import { Arrow } from 'react-konva';
import KonvaTextBox from '../components/KonvaTextBox';
import KonvaShape from '../components/KonvaShape';
import KonvaBoardImage from '../components/KonvaBoardImage';
import {
  updateMindNodePosition,
  setNodeParent,
  updateStickyColour,
  updateTextBoxPosition,
  updateShapePosition,
  addPath,
  addImage,
  updateImagePosition,
  updateShapeStyle,
  updateStickySize,
  updateStickyStyle,
  updateMindNodeSize,
  updateImageSize,
  updateStickyText,
} from '../utils/boardService';
import ColourPickerPopover from '../components/ColourPickerPopover';
import StickyNoteToolbar from '../components/StickyNoteToolbar';
import NodeToolbar from '../components/NodeToolbar';
import { updateMindNodeStyle } from '../utils/boardService';
import TextBoxToolbar from '../components/TextBoxToolbar';
import { updateTextBoxStyle } from '../utils/boardService';
import {
  FaStickyNote as FaStickyNoteIcon,
  FaProjectDiagram as FaProjectDiagramIcon,
  FaPen as FaPenIcon,
  FaImage as FaImageIcon,
  FaFont as FaFontIcon,
  FaShapes as FaShapesIcon,
  FaEraser as FaEraserIcon,
  FaMinus as FaMinusIcon,
  FaPlus as FaPlusIcon,
} from 'react-icons/fa';

// Cast react-icons components to strict JSX.Element-returning components to avoid TS2786
type IconComponent = React.FC<{ size?: number | string; color?: string }>;
const FaStickyNote = FaStickyNoteIcon as unknown as IconComponent;
const FaProjectDiagram = FaProjectDiagramIcon as unknown as IconComponent;
const FaPen = FaPenIcon as unknown as IconComponent;
const FaImage = FaImageIcon as unknown as IconComponent;
const FaFont = FaFontIcon as unknown as IconComponent;
const FaShapes = FaShapesIcon as unknown as IconComponent;
const FaEraser = FaEraserIcon as unknown as IconComponent;
const FaMinus = FaMinusIcon as unknown as IconComponent;
const FaPlus = FaPlusIcon as unknown as IconComponent;

const BrainstormingBoardPage: React.FC = () => {
  const { teamId, boardId: urlBoardId } = useParams<{ teamId: string; boardId?: string }>();
  const boardId = urlBoardId || 'data';
  const boardTag = `brainstorming:${boardId}`;
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;

  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [team, setTeam] = useState<any>(null);
  const [onlineMembers, setOnlineMembers] = useState<any[]>([]);
  const [board, setBoard] = useState<BrainstormingBoard | null>(null);
  const [newNoteText, setNewNoteText] = useState('');
  const [stageScale, setStageScale] = useState(1);
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState<{id:string;x:number;y:number}|null>(null);
  const [linkSource, setLinkSource] = useState<string|null>(null);
  const [colorPicker, setColorPicker] = useState<{id:string;x:number;y:number}|null>(null);
  const [selectedSticky, setSelectedSticky] = useState<{id:string; x:number; y:number} | null>(null);
  const [selectedText, setSelectedText] = useState<{id:string; x:number; y:number} | null>(null);
  const [selectedShape, setSelectedShape] = useState<string|null>(null);
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const [penMode, setPenMode] = useState<boolean>(false);
  const [drawing, setDrawing] = useState<boolean>(false);
  const [currentPoints, setCurrentPoints] = useState<number[]>([]);
  const [selectedImage, setSelectedImage] = useState<string|null>(null);
  // undo functionality removed
  const [eraserMode,setEraserMode] = useState<boolean>(false);
  const [eraserSize,setEraserSize] = useState<number>(20);
  const [showEraserMenu,setShowEraserMenu] = useState(false);
  const [erasing,setErasing] = useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const stageRef = React.useRef<any>(null);

  // Add helper function below state declarations
  const updateLocal = (collection:string,id:string,partial:any)=>{
    setBoard(prev=>{
      if(!prev) return prev;
      return {
        ...prev,
        [collection]:{
          ...(prev as any)[collection],
          [id]:{...((prev as any)[collection][id]||{}),...partial}
        }
      } as any;
    });
  };

  // Authorize
  useEffect(() => {
    if (!teamId || !currentUser?.uid) return;
    const teamRef = ref(database, `teams/${teamId}`);
    const unsub = onValue(teamRef, (snap) => {
      const data = snap.val();
      setTeam(data);
      if (data && data.members && data.members[currentUser.uid]) {
        setAuthorized(true);
      } else {
        setAuthorized(false);
      }
    });
    return () => unsub();
  }, [teamId, currentUser?.uid]);

  // Presence
  useEffect(() => {
    if (!teamId || !currentUser) return;
    
    const presRef = ref(database, `teams/${teamId}/presence/${currentUser.uid}`);
    
    // Function to update presence with current timestamp
    const updatePresence = () => {
      set(presRef, {
        uid: currentUser.uid,
        displayName: currentUser.displayName || 'User',
        photoURL: currentUser.photoURL || '',
        board: boardTag,
        lastActive: Date.now(),
      });
    };
    
    // Initial presence update
    updatePresence();
    
    // Set up heartbeat to update presence every 30 seconds
    const heartbeatInterval = setInterval(updatePresence, 30000);
    
    // Remove presence on disconnect
    onDisconnect(presRef).remove();
    
    // Clean up presence on beforeunload
    const handleBeforeUnload = () => {
      set(presRef, null);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    const listRef = ref(database, `teams/${teamId}/presence`);
    const unsub = onValue(listRef, (snap) => {
      const data = snap.val();
      if (data) {
        const currentTime = Date.now();
        const activeThreshold = 2 * 60 * 1000; // 2 minutes
        
        // Filter out stale presence data
        const activePresence: any = {};
        Object.entries(data).forEach(([uid, presence]: [string, any]) => {
          if (presence && presence.lastActive && (currentTime - presence.lastActive) < activeThreshold) {
            activePresence[uid] = presence;
          } else {
            // Remove stale presence data
            const staleRef = ref(database, `teams/${teamId}/presence/${uid}`);
            set(staleRef, null);
          }
        });
        
        setOnlineMembers(Object.values(activePresence));
      } else {
        setOnlineMembers([]);
      }
    });
    
    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      set(presRef, null); // Remove presence when component unmounts
      unsub();
    };
  }, [teamId, currentUser]);

  // Board subscription
  useEffect(() => {
    if (!teamId || authorized !== true || !currentUser) return;
    initBoardIfMissing(teamId, currentUser.uid, boardId);
    const unsub = subscribeToBoard(teamId, (data) => setBoard(data), boardId);
    return () => unsub();
  }, [teamId, authorized, currentUser, boardId]);

  // keyboard delete disabled per requirement

  if (authorized === false) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <p className="text-gray-500 mb-4">Access denied.</p>
        <button className="text-blue-600" onClick={() => navigate('/dashboard')}>Go back</button>
      </div>
    );
  }

  if (!board) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Handlers
  const addNote = async () => {
    if (!newNoteText.trim()) return;
    const id = uuidv4();
    const updated = {
      ...board.stickyNotes,
      [id]: { id, text: newNoteText, x: 100, y: 100, color: '#fef08a', width:150, height:100 },
    };
    setNewNoteText('');
    await updateBoard(teamId!, { stickyNotes: updated }, boardId);
  };

  const removeNote = async (id: string) => {
    const notes = { ...board.stickyNotes };
    delete notes[id];
    await updateBoard(teamId!, { stickyNotes: notes }, boardId);
  };

  const removeNode = async (id:string)=>{
     const nodes = { ...board.nodes } as any;
     delete nodes[id];
     // remove it as parent from any children
     Object.values(nodes).forEach((n:any)=>{ if(n.parentId===id) n.parentId=null; });
     await updateBoard(teamId!, { nodes } as any, boardId);
     setSelectedNode(null);
  };

  const addSticky = async () => {
    if (!teamId || !currentUser) return;
    const id = uuidv4();
    const { x: cx, y: cy } = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const sticky = {
      id,
      text: '',
      x: (cx - stagePos.x) / stageScale,
      y: (cy - stagePos.y) / stageScale,
      color: '#fef08a',
      fontSize: 14,
      bold: false,
      italic: false,
      author: currentUser.displayName || currentUser.email || 'unknown',
      width: 150,
      height: 100,
    };
    await updateBoard(teamId, { [`stickyNotes.${id}`]: sticky } as any, boardId);
  };

  const addNode = async ()=>{
     if(!teamId||!currentUser)return;
     const id = uuidv4();
     const {x:cx,y:cy} = {x:window.innerWidth/2, y:window.innerHeight/2};
     const node={id,text:'Idea',x:(cx-stagePos.x)/stageScale,y:(cy-stagePos.y)/stageScale,color:'#ddd6fe',width:180,height:60,fontSize:16,bold:false,italic:false,underline:false};
     await updateBoard(teamId,{[`nodes.${id}`]:node} as any, boardId);
  };

  const addTextBox = async () => {
    if (!teamId || !currentUser) return;
    const id = uuidv4();
    const { x: cx, y: cy } = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const textBox = {
      id,
      text: 'Text',
      x: (cx - stagePos.x) / stageScale,
      y: (cy - stagePos.y) / stageScale,
      color: '#111827',
      fontSize: 16,
      bold: false,
      italic: false,
    };
    await updateBoard(teamId, { [`textBoxes.${id}`]: textBox } as any, boardId);
  };

  const addShape = async (shapeType: 'rect' | 'circle' | 'square' | 'triangle') => {
    if (!teamId || !currentUser) return;
    const id = uuidv4();
    const { x: cx, y: cy } = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const size = 100;
    const shape = {
      id,
      type: shapeType,
      x: (cx - stagePos.x) / stageScale,
      y: (cy - stagePos.y) / stageScale,
      width: size,
      height: shapeType === 'rect' ? 80 : size,
      color: '#fda4af',
    } as any;
    await updateBoard(teamId, { [`shapes.${id}`]: shape } as any, boardId);
  };

  const handleWheel = (e: any) => {
    e.evt.preventDefault();
    const scaleBy = 1.05;
    const stage = e.target.getStage();
    const oldScale = stageScale;
    const mousePointTo = {
      x: (stage.getPointerPosition().x - stagePos.x) / oldScale,
      y: (stage.getPointerPosition().y - stagePos.y) / oldScale,
    };
    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    setStageScale(newScale);
    const newPos = {
      x: stage.getPointerPosition().x - mousePointTo.x * newScale,
      y: stage.getPointerPosition().y - mousePointTo.y * newScale,
    };
    setStagePos(newPos);
  };

  // Pen event handlers
  const getLocalPos = (stage:any) => {
    const pointer = stage.getPointerPosition();
    return {
      x: (pointer.x - stage.x()) / stage.scaleX(),
      y: (pointer.y - stage.y()) / stage.scaleY(),
    };
  };
  const handleStageMouseDown = (e:any) => {
    if(penMode){
      setDrawing(true);
      const stage = e.target.getStage();
      const {x,y} = getLocalPos(stage);
      setCurrentPoints([x,y]);
    } else if(eraserMode){
       setErasing(true);
       const stage = e.target.getStage();
       const {x,y} = getLocalPos(stage);
       eraseAt(x,y);
    } else {
      if(e.target===e.target.getStage()){
        setSelectedSticky(null); setSelectedText(null); setSelectedShape(null); setSelectedNode(null);
      }
    }
  };

  const handleStageMouseMove = (e:any) => {
    if(!penMode && !(eraserMode && erasing)) return;
    const stage = e.target.getStage();
    const {x,y} = getLocalPos(stage);
    if(penMode && drawing){
      setCurrentPoints(prev=>[...prev, x, y]);
    } else if(eraserMode && erasing){
      eraseAt(x,y);
    }
  };

  const handleStageMouseUp = async (e:any) => {
    if(penMode && drawing){
      setDrawing(false);
      if(currentPoints.length < 4){ setCurrentPoints([]); return; }
      if(!teamId) return;
      const id = uuidv4();
      const path = { id, points: currentPoints, color: '#0f172a', strokeWidth: 2 } as any;
      await addPath(teamId, path, boardId);
      setCurrentPoints([]);
    }
    if(eraserMode && erasing){
      setErasing(false);
    }
  };

  // Image upload handler
  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!teamId) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const storagePath = `boards/${teamId}/images/${file.name}-${Date.now()}`;
    const storageRef = (await import('firebase/storage')).ref;
    const { uploadBytes, getDownloadURL } = await import('firebase/storage');
    const ref = storageRef(storage, storagePath);
    await uploadBytes(ref, file);
    const url = await getDownloadURL(ref);
    const id = uuidv4();
    const { x: cx, y: cy } = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const img: any = {
      id,
      url,
      x: (cx - stagePos.x) / stageScale,
      y: (cy - stagePos.y) / stageScale,
      width: 200,
      height: 200,
    };
    await addImage(teamId, img, boardId);
  };

  const distToSeg = (px:number,py:number,x1:number,y1:number,x2:number,y2:number)=>{
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;
    const dot = A*C + B*D;
    const lenSq = C*C + D*D;
    let param = -1;
    if(lenSq!==0) param = dot/lenSq;
    let xx,yy;
    if(param<0){ xx=x1; yy=y1; }
    else if(param>1){ xx=x2; yy=y2; }
    else { xx = x1 + param*C; yy = y1 + param*D; }
    const dx = px-xx; const dy = py-yy;
    return Math.sqrt(dx*dx+dy*dy);
  };

  const eraseAt = async (x:number,y:number) => {
    if(!teamId || !board) return;
    const toDelete: string[] = [];
    Object.values(board.paths).forEach((p:any)=>{
      const pts = p.points;
      for(let i=0;i<pts.length-2;i+=2){
        const d = distToSeg(x,y,pts[i],pts[i+1],pts[i+2],pts[i+3]);
        if(d <= eraserSize/stageScale){ toDelete.push(p.id); break; }
      }
    });
    if(toDelete.length){
      const updated = {...board.paths};
      toDelete.forEach(id=>{
        // no undo storage
        delete updated[id];
      });
      await updateBoard(teamId, { paths: updated }, boardId);
    }
  };

  // define removeTextBox after removeNote
  const removeTextBox = async (id:string)=>{
     const updated = {...board!.textBoxes};
     delete updated[id];
     await updateBoard(teamId!, { textBoxes: updated }, boardId);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4 bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200">
        <h1 className="text-xl font-bold text-gray-900 cursor-pointer" onClick={() => navigate(`/teams/${teamId}/brainstorming`)}>
          {(board as any).title || 'Brainstorming Board'}
        </h1>
        {/* Avatars */}
        <div className="flex -space-x-2">
          {onlineMembers.filter((m:any)=>m.uid!==currentUser?.uid && m.board===boardTag).map((m:any, idx:number) => (
            <img key={idx} src={m.photoURL || defaultProfile} alt="avatar" className="w-8 h-8 rounded-full border-2 border-white" />
          ))}
        </div>
      </header>

      {/* Toolbar */}
      <div className="fixed top-20 left-4 z-50 flex flex-col gap-3">
        {/* Sticky */}
        <button onClick={addSticky} title="Add sticky note" className="bg-blue-500 text-white w-10 h-10 flex items-center justify-center rounded-full shadow hover:bg-blue-600">
          <FaStickyNote size={18} />
        </button>
        {/* Node */}
        <button onClick={addNode} title="Add node" className="bg-purple-500 text-white w-10 h-10 flex items-center justify-center rounded-full shadow hover:bg-purple-600">
          <FaProjectDiagram size={18} />
        </button>
        {/* Pen toggle */}
        <button onClick={()=>setPenMode(p=>!p)} title="Pen" className={`w-10 h-10 flex items-center justify-center rounded-full shadow ${penMode? 'bg-yellow-500 text-white hover:bg-yellow-600':'bg-white text-gray-700 border hover:bg-gray-100'}`}>
          <FaPen size={16} />
        </button>
        {/* Image upload */}
        <button onClick={()=>fileInputRef.current?.click()} title="Add image" className="bg-indigo-500 text-white w-10 h-10 flex items-center justify-center rounded-full shadow hover:bg-indigo-600">
          <FaImage size={18} />
        </button>
        <input type="file" accept="image/*" ref={fileInputRef} onChange={handleImageSelect} className="hidden" />
        {/* Text box */}
        <button onClick={addTextBox} title="Add text box" className="bg-emerald-500 text-white w-10 h-10 flex items-center justify-center rounded-full shadow hover:bg-emerald-600">
          <FaFont size={18} />
        </button>

        {/* Shape dropdown */}
        <div className="relative">
          <button title="Add shape" className="bg-pink-500 text-white w-10 h-10 flex items-center justify-center rounded-full shadow hover:bg-pink-600" onClick={()=>setShowShapeMenu(m=>!m)}>
            <FaShapes size={18} />
          </button>
          {showShapeMenu && (
            <div className="absolute left-12 top-0 bg-white border shadow rounded z-50 text-gray-700 p-2 flex flex-col gap-1" onMouseLeave={()=>setShowShapeMenu(false)}>
              <button className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 rounded" onClick={()=>{addShape('rect'); setShowShapeMenu(false);}}>
                <svg width="18" height="14"><rect width="18" height="14" fill="#fda4af"/></svg><span className="text-sm">Rectangle</span>
              </button>
              <button className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 rounded" onClick={()=>{addShape('square'); setShowShapeMenu(false);}}>
                <svg width="16" height="16"><rect width="16" height="16" fill="#fda4af"/></svg><span className="text-sm">Square</span>
              </button>
              <button className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 rounded" onClick={()=>{addShape('circle'); setShowShapeMenu(false);}}>
                <svg width="16" height="16"><circle cx="8" cy="8" r="8" fill="#fda4af"/></svg><span className="text-sm">Circle</span>
              </button>
              <button className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 rounded" onClick={()=>{addShape('triangle'); setShowShapeMenu(false);}}>
                <svg width="18" height="16" viewBox="0 0 18 16"><polygon points="9,0 18,16 0,16" fill="#fda4af"/></svg><span className="text-sm">Triangle</span>
              </button>
            </div>
          )}
        </div>

        {/* Eraser dropdown */}
        <div className="relative">
          <button title="Eraser" className={`w-10 h-10 flex items-center justify-center rounded-full shadow ${eraserMode?'bg-red-500 text-white hover:bg-red-600':'bg-white text-gray-700 border hover:bg-gray-100'}`} onClick={()=>{setShowEraserMenu(m=>!m); setPenMode(false); setEraserMode(true);}}>
            <FaEraser size={18} />
          </button>
          {showEraserMenu && (
            <div className="absolute left-12 top-0 bg-white border shadow rounded z-50 text-gray-700" onMouseLeave={()=>setShowEraserMenu(false)}>
              {[[10,'Small'],[20,'Medium'],[30,'Large']].map(([sz,label])=> (
                <button key={label} className="block px-4 py-2 hover:bg-gray-100 w-full text-left" onClick={()=>{setEraserSize(sz as number); setShowEraserMenu(false);}}>{label}</button>
              ))}
            </div>
          )}
        </div>

        {/* Zoom controls */}
        <div className="flex flex-col items-center gap-1 mt-2 bg-white/80 p-2 rounded shadow text-gray-800">
          <button onClick={()=>setStageScale(s=>s/1.1)} className="hover:text-blue-600"><FaMinus /></button>
          <span className="text-xs font-medium">{Math.round(stageScale*100)}%</span>
          <button onClick={()=>setStageScale(s=>s*1.1)} className="hover:text-blue-600"><FaPlus /></button>
        </div>
      </div>

      {/* Canvas simple list for MVP */}
      <Stage ref={stageRef} width={window.innerWidth} height={window.innerHeight} scaleX={stageScale} scaleY={stageScale} x={stagePos.x} y={stagePos.y} style={{ cursor: penMode ? 'url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20width%3D%2720%27%20height%3D%2720%27%20viewBox%3D%270%200%2024%2024%27%20fill%3D%27%230f172a%27%3E%3Cpath%20d%3D%27M21.7%207.3l-5-5a1%201%200%2000-1.4%200L3%2014.6V21h6.4L21.7%208.7a1%201%200%20000-1.4zM9.2%2019H5v-4.2l11-11L20.2%208l-11%2011z%27/%3E%3C/svg%3E")%2010%2010,%20crosshair' : eraserMode ? 'url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20width%3D%2720%27%20height%3D%2720%27%20viewBox%3D%270%200%2024%2024%27%20fill%3D%27%23ef4444%27%3E%3Crect%20x%3D%274%27%20y%3D%274%27%20width%3D%2716%27%20height%3D%2716%27%20rx%3D%223%22/%3E%3C/svg%3E")%2010%2010,%20crosshair' : 'default' }} onWheel={handleWheel} draggable={!penMode && !eraserMode} onMouseDown={handleStageMouseDown} onMouseMove={handleStageMouseMove} onMouseUp={handleStageMouseUp} >
        <Layer>
          {/* saved paths */}
          {board.paths && Object.values(board.paths).map((p:any)=>(
            <Line key={p.id} points={p.points} stroke={p.color} strokeWidth={p.strokeWidth} lineCap="round" lineJoin="round"
               onClick={eraserMode ? async ()=>{
                 // add to undo and remove
                 // no undo storage
                 const updated = {...board.paths};
                 delete updated[p.id];
                 await updateBoard(teamId!, { paths: updated }, boardId);
               }: undefined}
               listening={eraserMode}
            />
          ))}
          {/* temp drawing */}
          {penMode && drawing && currentPoints.length>0 && (
            <Line points={currentPoints} stroke="#0f172a" strokeWidth={2} lineCap="round" lineJoin="round" />
          )}
        </Layer>
        <Layer>
          {board.images && Object.values(board.images).map((img:any)=>(
            <KonvaBoardImage
              key={img.id}
              {...img}
              isSelected={selectedImage===img.id}
              onMove={(iid,x,y)=>updateImagePosition(teamId!,iid,x,y,boardId)}
              onSelect={(iid)=>{ setSelectedImage(iid); setSelectedNode(null);} }
              onResize={(iid,w,h)=>{ updateLocal('images',iid,{width:w,height:h}); updateImageSize(teamId!,iid,w,h,boardId); }}
            />
          ))}
        </Layer>
        <Layer>
          {/* arrows */}
          {board.nodes && Object.values(board.nodes).map(child=>{
             if(!child.parentId) return null;
             const parent= board.nodes[child.parentId];
             if(!parent) return null;
             const points=[parent.x+90,parent.y+30,child.x+90,child.y+30];
             return <Arrow key={`${parent.id}-${child.id}`} points={points} stroke="#9ca3af" pointerLength={6} pointerWidth={6}/>;
          })}
        </Layer>
        <Layer>
          {/* Shapes */}
          {board.shapes && Object.values(board.shapes).map((sh:any)=>{
            return (
              <KonvaShape
                 key={sh.id}
                 {...sh}
                 isSelected={selectedShape===sh.id}
                 onMove={(sid,x,y)=>updateShapePosition(teamId!,sid,x,y,boardId)}
                 onSelect={(sid)=>{ setSelectedShape(sid); setSelectedNode(null);} }
                 onResize={(sid,w,h)=>{ updateLocal('shapes',sid,{width:w,height:h}); updateShapeStyle(teamId!,sid,{width:w,height:h},boardId); }}
              />
            );
          })}

          {/* Text boxes */}
          {board.textBoxes && Object.values(board.textBoxes).map((tb:any)=>{
            return (
              <KonvaTextBox
                key={tb.id}
                {...tb}
                isSelected={selectedText?.id===tb.id}
                onMove={(tid,x,y)=>updateTextBoxPosition(teamId!,tid,x,y,boardId)}
                onSelect={(tid)=>{ 
                  const toolbarGap = 8;
                  const toolbarHeight = 40;
                  const screenX = tb.x*stageScale + stagePos.x;
                  const screenY = tb.y*stageScale + stagePos.y - toolbarGap - toolbarHeight;
                  setSelectedText({id:tid, x:screenX, y:screenY}); 
                  setSelectedNode(null);
                  setSelectedSticky(null);
                  setSelectedShape(null);
                }}
                stageScale={stageScale}
              />
            );
          })}

          {Object.values(board.stickyNotes).map(note => {
            const n = note as any;
            return (
              <KonvaStickyNote
                key={note.id}
                id={note.id}
                text={note.text}
                x={note.x}
                y={note.y}
                color={n.color || '#fef08a'}
                fontSize={n.fontSize || 14}
                bold={n.bold || false}
                italic={n.italic || false}
                underline={n.underline || false}
                fontFamily={n.fontFamily || 'Inter'}
                listStyle={n.listStyle || 'none'}
                author={n.author}
                isSelected={selectedSticky?.id===note.id}
                onMove={(nid,nx,ny)=>updateStickyNotePosition(teamId!,nid,nx,ny,boardId)}
                onDelete={removeNote}
                onColorRequest={(id,x,y)=>setColorPicker({id,x,y})}
                onSelect={(id)=>{
                  setSelectedNode(null);
                  const noteHeight = (n.height || 100)*stageScale;
                  const toolbarGap = 8;
                  const screenX = note.x*stageScale + stagePos.x;
                  const screenY = note.y*stageScale + stagePos.y - toolbarGap - 40; // approximate 40px toolbar height
                  setSelectedSticky({id,x:screenX,y:screenY});
                }}
                stageScale={stageScale}
                stageOffset={stagePos}
                width={n.width || 150}
                height={n.height || 100}
                onResize={(nid,w,h)=>{ updateLocal('stickyNotes',nid,{width:w,height:h}); updateStickySize(teamId!,nid,w,h,boardId); }}
                onTextChange={(nid,text)=>{ updateLocal('stickyNotes',nid,{text}); updateStickyText(teamId!,nid,text,boardId); }}
              />
            );
          })}
          {board.nodes && Object.values(board.nodes).map(node=>{
              const n = node as any;
              const nodeProps = { ...n, color: n.color || '#ddd6fe', fontSize: n.fontSize || 16, bold: n.bold || false, italic: n.italic || false, underline: n.underline || false };
              const isSel = selectedNode?.id===node.id;
              return (
                <KonvaMindNode
                  key={node.id}
                  {...nodeProps}
                  isSelected={isSel}
                  stageScale={stageScale}
                  onMove={(nid,x,y)=>{ updateMindNodePosition(teamId!,nid,x,y,boardId); if(selectedNode && selectedNode.id===nid){ const toolbarGap=8; const toolbarHeight=32; const screenX=x*stageScale+stagePos.x; const screenY=y*stageScale+stagePos.y - toolbarGap - toolbarHeight; setSelectedNode({...selectedNode,x:screenX,y:screenY}); } }}
                  onSelect={(nid)=>{
                   if(linkSource && linkSource!==nid){ setNodeParent(teamId!, nid, linkSource, boardId); setLinkSource(null);} else {
                      const nodeData = board.nodes[nid] as any;
                      const toolbarGap = 8;
                      const toolbarHeight = 32; // approximate toolbar height
                      const screenX = nodeData.x*stageScale + stagePos.x;
                      const screenY = nodeData.y*stageScale + stagePos.y - toolbarGap - toolbarHeight;
                      setSelectedNode({id:nid,x:screenX,y:screenY});
                      setLinkSource(nid);
                   } }}
                  width={n.width || 180}
                  height={n.height || 60}
                  onResize={(nid,w,h)=>{ updateLocal('nodes',nid,{width:w,height:h}); updateMindNodeSize(teamId!,nid,w,h,boardId); }}
                />
             );
           })}
        </Layer>
      </Stage>
      {selectedSticky && board.stickyNotes[selectedSticky.id] && (
     <StickyNoteToolbar
         x={selectedSticky.x}
         y={selectedSticky.y}
         colour={(board.stickyNotes[selectedSticky.id] as any).color}
         bold={(board.stickyNotes[selectedSticky.id] as any).bold}
         italic={(board.stickyNotes[selectedSticky.id] as any).italic}
         underline={(board.stickyNotes[selectedSticky.id] as any).underline}
         fontFamily={(board.stickyNotes[selectedSticky.id] as any).fontFamily || 'Inter'}
         listStyle={(board.stickyNotes[selectedSticky.id] as any).listStyle || 'none'}
         fontSize={(board.stickyNotes[selectedSticky.id] as any).fontSize}
         onChange={(partial)=>{
            if(partial.listStyle){
              const currentText = (board.stickyNotes[selectedSticky.id] as any).text || '';
              let newText=currentText;
              if(partial.listStyle==='bullet' && !currentText.startsWith('• ')) newText = `• ${currentText}`;
              if(partial.listStyle==='none' && currentText.startsWith('• ')) newText = currentText.slice(2);
              updateStickyText(teamId!, selectedSticky.id, newText, boardId);
            }
            updateStickyStyle(teamId!, selectedSticky.id, partial, boardId);
         }}
         onDelete={()=>{ removeNote(selectedSticky.id); setSelectedSticky(null);} }
         onClose={()=>setSelectedSticky(null)}
     />
  )}
      {selectedText && board.textBoxes[selectedText.id] && (
        <TextBoxToolbar
          x={selectedText.x}
          y={selectedText.y}
          colour={(board.textBoxes[selectedText.id] as any).color}
          bold={(board.textBoxes[selectedText.id] as any).bold}
          italic={(board.textBoxes[selectedText.id] as any).italic}
          underline={(board.textBoxes[selectedText.id] as any).underline}
          fontSize={(board.textBoxes[selectedText.id] as any).fontSize}
          onChange={(partial)=>{ updateLocal('textBoxes',selectedText.id,partial); updateTextBoxStyle(teamId!,selectedText.id,partial,boardId); }}
          onDelete={()=>{ removeTextBox(selectedText.id); setSelectedText(null);} }
          onClose={()=>setSelectedText(null)}
        />
      )}
      {colorPicker && (
         <ColourPickerPopover x={colorPicker.x} y={colorPicker.y} onClose={()=>setColorPicker(null)} onSelect={(c)=>updateStickyColour(teamId!,colorPicker.id,c,boardId)} />
      )}

      {selectedNode && board.nodes[selectedNode.id] && (
        <NodeToolbar
          x={selectedNode.x}
          y={selectedNode.y}
          colour={(board.nodes[selectedNode.id] as any).color}
          fontSize={(board.nodes[selectedNode.id] as any).fontSize || 16}
          bold={(board.nodes[selectedNode.id] as any).bold || false}
          italic={(board.nodes[selectedNode.id] as any).italic || false}
          underline={(board.nodes[selectedNode.id] as any).underline || false}
          onChange={(partial)=>{ updateLocal('nodes',selectedNode.id,partial); updateMindNodeStyle(teamId!,selectedNode.id,partial,boardId);} }
          onDelete={()=>removeNode(selectedNode.id)}
          onClose={()=>setSelectedNode(null)}
        />
      )}
    </div>
  );
};

export default BrainstormingBoardPage; 